// F-063, F-064 : incidents (déclaration, tri, historique, photos), devis, interventions et factures.
import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { patch, post } from "@core/api";
import { useGet, useInvalidate } from "@core/hooks";
import { date, dateTime, money } from "@core/format";
import { useOrg } from "@core/org";
import { Badge, Button, Card, Checkbox, Empty, ErrorBox, FileDrop, Input, KeyValues, Loading, Modal, MoneyInput, Notice, PageHeader, Select, Table, Tabs, Textarea, fieldError } from "@core/ui";
import {
  BackLink,
  CATEGORIES,
  DocLinks,
  INCIDENT_STATUS,
  INTERVENTION_STATUS,
  LeaseSelect,
  PRIORITIES,
  PropertySelect,
  QUOTE_STATUS,
  RECOVERABLE,
  Status,
  Timeline,
  URGENCIES,
  UrgencyBadge,
  addDays,
  docUrl,
  meLease,
  options,
  orgApi,
  sendFile,
  toIso,
  todayIso,
  useAction,
} from "./shared";
import { ClaimsList } from "./claims";
import { OrgAuthorizations } from "./authorizations";
import { TenantEquipments } from "./maintenance";

type Slot = { date: string; from: string; to: string; note: string };

/** Formulaire de déclaration (locataire, délégué ou bailleur selon le chemin). */
export function IncidentForm({ path, onCreated, extra }: { path: string; onCreated: (id: string, notice?: string) => void; extra?: Record<string, string> }) {
  const [form, setForm] = useState({ category: "plomberie", urgency: "normale", title: "", description: "", access_notes: "" });
  const [slots, setSlots] = useState<Slot[]>([]);
  const act = useAction();
  return (
    <form
      className="stack"
      onSubmit={async (e) => {
        e.preventDefault();
        const r = await act.run(() => post<{ id: string; emergency_notice?: string }>(path, { ...form, ...extra, availability: slots }), "Incident signalé.");
        if (r) {
          setForm({ category: "plomberie", urgency: "normale", title: "", description: "", access_notes: "" });
          setSlots([]);
          onCreated(r.id, r.emergency_notice ?? undefined);
        }
      }}
    >
      <ErrorBox error={act.error} />
      <div className="form-row">
        <Select label="Catégorie" value={form.category} options={options(CATEGORIES)} onChange={(e) => setForm({ ...form, category: e.target.value })} />
        <Select label="Urgence" value={form.urgency} options={options(URGENCIES)} onChange={(e) => setForm({ ...form, urgency: e.target.value })} />
      </div>
      {form.urgency === "urgente" ? (
        <Notice tone="attention" title="Urgence">
          En cas de danger immédiat (odeur de gaz, incendie, risque électrique), appelez le 112. Pour une fuite importante, coupez l’arrivée d’eau générale.
        </Notice>
      ) : null}
      <Input label="Titre" required value={form.title} error={fieldError(act.error, "title")} placeholder="Ex. Fuite sous l’évier" onChange={(e) => setForm({ ...form, title: e.target.value })} />
      <Textarea label="Description" value={form.description} error={fieldError(act.error, "description")} onChange={(e) => setForm({ ...form, description: e.target.value })} />
      <fieldset className="stack-sm">
        <legend>Disponibilités pour laisser entrer un intervenant</legend>
        {slots.map((s, i) => (
          <div key={i} className="vie-slot-row">
            <Input label="Date" type="date" value={s.date} onChange={(e) => setSlots(slots.map((x, j) => (j === i ? { ...x, date: e.target.value } : x)))} />
            <Input label="De" type="time" value={s.from} onChange={(e) => setSlots(slots.map((x, j) => (j === i ? { ...x, from: e.target.value } : x)))} />
            <Input label="À" type="time" value={s.to} onChange={(e) => setSlots(slots.map((x, j) => (j === i ? { ...x, to: e.target.value } : x)))} />
            <Button small variant="discret" onClick={() => setSlots(slots.filter((_, j) => j !== i))}>
              Retirer
            </Button>
          </div>
        ))}
        {fieldError(act.error, "availability") ? <p className="bv-champ-aide" role="alert">{fieldError(act.error, "availability")}</p> : null}
        <div>
          <Button small variant="secondaire" onClick={() => setSlots([...slots, { date: addDays(todayIso(), 1), from: "18:00", to: "20:00", note: "" }])}>
            Ajouter un créneau
          </Button>
        </div>
      </fieldset>
      <Input label="Informations d’accès (facultatif)" value={form.access_notes} help="Ne notez pas de code d’accès : communiquez-le directement à l’intervenant." onChange={(e) => setForm({ ...form, access_notes: e.target.value })} />
      <div className="row">
        <Button type="submit" busy={act.busy}>
          Signaler l’incident
        </Button>
      </div>
    </form>
  );
}

function IncidentTable({ items, link }: { items: any[]; link: (id: string) => string }) {
  if (!items.length) return <Empty title="Aucun incident">Les signalements des locataires et les vôtres apparaîtront ici.</Empty>;
  return (
    <Table caption="Incidents">
      <thead>
        <tr>
          <th>Incident</th>
          <th>Logement</th>
          <th>Urgence</th>
          <th>Statut</th>
          <th>Signalé le</th>
        </tr>
      </thead>
      <tbody>
        {items.map((i) => (
          <tr key={i.id}>
            <td>
              <Link to={link(i.id)}>{i.title}</Link>
              <span className="block small muted">
                {CATEGORIES[i.category]} · {i.reporter_label}
                {i.pending_quotes ? ` · ${i.pending_quotes} devis à décider` : ""}
              </span>
            </td>
            <td>{i.property_label}</td>
            <td>
              <UrgencyBadge value={i.urgency} />
            </td>
            <td>
              <Status value={i.status} labels={INCIDENT_STATUS} />
            </td>
            <td className="num">{date(i.created_at)}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function NewIncidentModal({ orgId, onCreated }: { orgId: string; onCreated: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState({ lease_id: "", property_id: "" });
  return (
    <>
      <Button onClick={() => setOpen(true)}>Nouvel incident</Button>
      <Modal open={open} onClose={() => setOpen(false)} title="Nouvel incident">
        <div className="stack">
          <LeaseSelect orgId={orgId} value={target.lease_id} label="Bail concerné (facultatif)" onChange={(v) => setTarget({ ...target, lease_id: v })} />
          {!target.lease_id ? <PropertySelect orgId={orgId} value={target.property_id} onChange={(v) => setTarget({ ...target, property_id: v })} /> : null}
          <IncidentForm
            path={orgApi(orgId, "/incidents")}
            extra={target.lease_id ? { lease_id: target.lease_id } : target.property_id ? { property_id: target.property_id } : {}}
            onCreated={(id) => {
              setOpen(false);
              onCreated(id);
            }}
          />
        </div>
      </Modal>
    </>
  );
}

/** Page « Incidents » : incidents, sinistres, demandes d'autorisation. */
export function IncidentsPage() {
  const org = useOrg();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = (params.get("onglet") as "incidents" | "sinistres" | "autorisations") || "incidents";
  const [open, setOpen] = useState(true);
  const list = useGet<{ items: any[] }>(tab === "incidents" ? orgApi(org.orgId, `/incidents${open ? "?open=true" : ""}`) : null);
  return (
    <div className="page">
      <PageHeader
        eyebrow="Vie du logement"
        title="Incidents"
        intro="Signalements, devis et interventions. La qualification d’un incident ne détermine pas la responsabilité : vous décidez."
        actions={org.can("operate") && tab === "incidents" ? <NewIncidentModal orgId={org.orgId} onCreated={(id) => nav(org.to(`incidents/${id}`))} /> : null}
      />
      <Tabs
        value={tab}
        onChange={(v) => setParams(v === "incidents" ? {} : { onglet: v })}
        items={[
          { value: "incidents", label: "Incidents" },
          { value: "sinistres", label: "Sinistres" },
          { value: "autorisations", label: "Demandes d’autorisation" },
        ]}
      />
      {tab === "incidents" ? (
        <>
          <Checkbox label="Afficher seulement les incidents en cours" checked={open} onChange={setOpen} />
          {list.isLoading ? <Loading /> : <IncidentTable items={list.data?.items ?? []} link={(id) => org.to(`incidents/${id}`)} />}
        </>
      ) : tab === "sinistres" ? (
        <ClaimsList orgId={org.orgId} />
      ) : (
        <OrgAuthorizations orgId={org.orgId} />
      )}
    </div>
  );
}

/** Fiche d'un incident côté bailleur. */
export function IncidentPage() {
  const org = useOrg();
  const nav = useNavigate();
  const { incidentId = "" } = useParams();
  const path = orgApi(org.orgId, `/incidents/${incidentId}`);
  const q = useGet<any>(path);
  const invalidate = useInvalidate();
  const refresh = () => invalidate(path, orgApi(org.orgId, "/incidents"));
  const [upd, setUpd] = useState({ status: "", priority: "", comment: "", internal: false });
  const act = useAction();
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const { incident: i, events, documents, quotes, interventions, claims, conversations } = q.data;
  const canOp = org.can("operate");
  return (
    <div className="page">
      <BackLink to={org.to("incidents")}>Incidents</BackLink>
      <PageHeader
        eyebrow={i.property_label}
        title={i.title}
        intro={`${CATEGORIES[i.category]} · signalé par ${i.reporter_label} le ${dateTime(i.created_at)}`}
        actions={
          <>
            <UrgencyBadge value={i.urgency} />
            <Status value={i.status} labels={INCIDENT_STATUS} />
          </>
        }
      />
      {q.data.emergency_notice ? <Notice tone="attention">{q.data.emergency_notice}</Notice> : null}
      <div className="grid-2">
        <Card title="Signalement">
          <KeyValues
            items={[
              ["Priorité", PRIORITIES[i.priority]],
              ["Description", i.description || "—"],
              ["Accès", i.access_notes || "—"],
              ["Disponibilités", i.availability?.length ? i.availability.map((s: any) => `${date(s.date)} de ${s.from} à ${s.to}`).join(" ; ") : "Non précisées"],
              ["Confirmation du locataire", i.tenant_confirmed_at ? `Résolution confirmée le ${dateTime(i.tenant_confirmed_at)}` : "—"],
            ]}
          />
          <DocLinks docs={documents} />
        </Card>
        {canOp ? (
          <Card title="Répondre et suivre">
            <form
              className="stack"
              onSubmit={async (e) => {
                e.preventDefault();
                if (upd.internal && upd.comment.trim()) {
                  await act.run(() => post(`${path}/comments`, { body: upd.comment, visible_to_tenant: false }), "Note interne ajoutée.");
                  if (upd.status || upd.priority) await act.run(() => patch(path, { status: upd.status || undefined, priority: upd.priority || undefined }));
                } else {
                  await act.run(() => patch(path, { status: upd.status || undefined, priority: upd.priority || undefined, comment: upd.comment }), "Suivi enregistré.");
                }
                setUpd({ status: "", priority: "", comment: "", internal: false });
                refresh();
              }}
            >
              <ErrorBox error={act.error} />
              <Select label="Nouveau statut" value={upd.status} onChange={(e) => setUpd({ ...upd, status: e.target.value })} options={[{ value: "", label: "Inchangé" }, ...options(INCIDENT_STATUS)]} />
              <Select label="Priorité" value={upd.priority} onChange={(e) => setUpd({ ...upd, priority: e.target.value })} options={[{ value: "", label: "Inchangée" }, ...options(PRIORITIES)]} />
              <Textarea label="Réponse ou commentaire" value={upd.comment} error={fieldError(act.error, "comment")} onChange={(e) => setUpd({ ...upd, comment: e.target.value })} />
              <Checkbox label="Note interne (non visible du locataire)" checked={upd.internal} onChange={(v) => setUpd({ ...upd, internal: v })} />
              <Button type="submit" busy={act.busy}>
                Enregistrer
              </Button>
            </form>
          </Card>
        ) : null}
      </div>
      <Card
        title="Échanges liés"
        actions={
          canOp ? (
            <Button
              small
              variant="secondaire"
              busy={act.busy}
              onClick={async () => {
                const target = i.lease_id ? { lease_id: i.lease_id } : { property_id: i.property_id };
                const r = await act.run(
                  () => post<{ id: string }>(orgApi(org.orgId, "/conversations"), { ...target, incident_id: incidentId, subject: `Incident : ${i.title}`, body: "Bonjour, je reviens vers vous au sujet de cet incident." }),
                  "Conversation ouverte.",
                );
                if (r) nav(org.to(`messages/${r.id}`));
              }}
            >
              {i.lease_id ? "Écrire au locataire" : "Ouvrir une note interne"}
            </Button>
          ) : null
        }
      >
        {conversations.length ? (
          <ul className="list-plain">
            {conversations.map((c: any) => (
              <li key={c.id}>
                <Link to={org.to(`messages/${c.id}`)}>{c.subject}</Link> <span className="small muted">{c.shared_with_tenant ? "· partagé avec le locataire" : "· interne"}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted small">Aucun échange rattaché à cet incident.</p>
        )}
      </Card>
      <Card title="Historique">
        <Timeline
          items={events.map((e: any) => ({
            id: e.id,
            at: e.created_at,
            who: e.author_label,
            text: e.body,
            tag: !e.visible_to_tenant ? <Badge>Interne</Badge> : null,
          }))}
        />
      </Card>
      <QuotesCard orgId={org.orgId} incidentId={incidentId} quotes={quotes} canOp={canOp} canFinance={org.can("finance")} onChange={refresh} />
      <InterventionsCard orgId={org.orgId} incidentId={incidentId} quotes={quotes} interventions={interventions} canOp={canOp} onChange={refresh} />
      {canOp ? (
        <Card title="Pièces">
          <IncidentUpload path={`${path}/documents`} onDone={refresh} />
        </Card>
      ) : null}
      <Card
        title="Sinistre"
        actions={
          canOp && !claims.length ? (
            <Button
              small
              variant="secondaire"
              onClick={async () => {
                const r = await act.run(() => post<{ id: string }>(orgApi(org.orgId, "/claims"), { incident_id: incidentId, kind: i.category === "plomberie" ? "degat_des_eaux" : "autre", title: i.title }), "Dossier de sinistre ouvert.");
                if (r) nav(org.to(`sinistres/${r.id}`));
              }}
            >
              Ouvrir un dossier de sinistre
            </Button>
          ) : null
        }
      >
        {claims.length ? (
          <ul className="list-plain">
            {claims.map((c: any) => (
              <li key={c.id}>
                <Link to={org.to(`sinistres/${c.id}`)}>{c.title}</Link> <span className="small muted">· {c.status}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted small">Un incident n’est pas nécessairement un sinistre couvert : ouvrez un dossier seulement si une déclaration d’assurance est envisagée.</p>
        )}
      </Card>
      <p className="small muted">{q.data.rappel}</p>
    </div>
  );
}

function IncidentUpload({ path, onDone }: { path: string; onDone: () => void }) {
  const [kind, setKind] = useState("photo");
  const [visible, setVisible] = useState(true);
  const act = useAction();
  return (
    <div className="stack-sm">
      <ErrorBox error={act.error} />
      <div className="form-row">
        <Select
          label="Type de pièce"
          value={kind}
          onChange={(e) => {
            setKind(e.target.value);
            setVisible(["photo", "compte_rendu"].includes(e.target.value));
          }}
          options={[
            { value: "photo", label: "Photo" },
            { value: "devis", label: "Devis" },
            { value: "facture", label: "Facture" },
            { value: "compte_rendu", label: "Compte rendu d’intervention" },
            { value: "autre", label: "Autre" },
          ]}
        />
        <Checkbox label="Visible du locataire" checked={visible} onChange={setVisible} />
      </div>
      <FileDrop
        busy={act.busy}
        onFiles={async (files) => {
          for (const f of files) await act.run(() => sendFile(path, f, { kind, visible_to_tenant: String(visible) }), "Pièce ajoutée.");
          onDone();
        }}
      />
    </div>
  );
}

function QuotesCard({ orgId, incidentId, quotes, canOp, canFinance, onChange }: { orgId: string; incidentId: string; quotes: any[]; canOp: boolean; canFinance: boolean; onChange: () => void }) {
  const [form, setForm] = useState({ contractor_name: "", contractor_contact: "", description: "", amount_cents: null as number | null });
  const [doc, setDoc] = useState<{ id: string; filename: string } | null>(null);
  const act = useAction();
  return (
    <Card title="Devis">
      <ErrorBox error={act.error} />
      {quotes.length ? (
        <Table caption="Devis">
          <thead>
            <tr>
              <th>Intervenant</th>
              <th className="right">Montant</th>
              <th>Statut</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {quotes.map((q) => (
              <tr key={q.id}>
                <td>
                  {q.contractor_name}
                  <span className="block small muted">{q.description}</span>
                  {q.document_id ? (
                    <a className="small" href={docUrl(q.document_id)} target="_blank" rel="noreferrer">
                      Voir le devis
                    </a>
                  ) : null}
                </td>
                <td className="vie-amount">{money(q.amount_cents)}</td>
                <td>
                  <Status value={q.status} labels={QUOTE_STATUS} />
                  {q.decided_by_name ? <span className="block small muted">par {q.decided_by_name}</span> : null}
                </td>
                <td>
                  {canFinance && q.status === "recu" ? (
                    <div className="row">
                      <Button small onClick={async () => { await act.run(() => post(orgApi(orgId, `/quotes/${q.id}/decision`), { decision: "accepte" }), "Devis accepté. Les travaux restent à réaliser."); onChange(); }}>
                        Accepter
                      </Button>
                      <Button small variant="secondaire" onClick={async () => { await act.run(() => post(orgApi(orgId, `/quotes/${q.id}/decision`), { decision: "refuse" }), "Devis refusé."); onChange(); }}>
                        Refuser
                      </Button>
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <p className="muted">Aucun devis.</p>
      )}
      {!canFinance && quotes.some((q) => q.status === "recu") ? <p className="small muted">L’acceptation d’un devis est réservée aux membres qui gèrent les finances.</p> : null}
      {canOp ? (
        <form
          className="stack-sm"
          onSubmit={async (e) => {
            e.preventDefault();
            const r = await act.run(() => post(orgApi(orgId, `/incidents/${incidentId}/quotes`), { ...form, amount_cents: form.amount_cents ?? 0, document_id: doc?.id }), "Devis enregistré.");
            if (r) {
              setForm({ contractor_name: "", contractor_contact: "", description: "", amount_cents: null });
              setDoc(null);
              onChange();
            }
          }}
        >
          <h3>Ajouter un devis</h3>
          <div className="form-row">
            <Input label="Intervenant" required value={form.contractor_name} error={fieldError(act.error, "contractor_name")} onChange={(e) => setForm({ ...form, contractor_name: e.target.value })} />
            <Input label="Contact" value={form.contractor_contact} onChange={(e) => setForm({ ...form, contractor_contact: e.target.value })} />
            <MoneyInput label="Montant TTC" cents={form.amount_cents} error={fieldError(act.error, "amount_cents")} onChange={(c) => setForm({ ...form, amount_cents: c })} />
          </div>
          <Input label="Objet" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <FileDrop
            label={doc ? `Devis joint : ${doc.filename}` : "Joindre le devis"}
            onFiles={async ([f]) => {
              const r = await act.run(() => sendFile(orgApi(orgId, `/incidents/${incidentId}/documents`), f, { kind: "devis", visible_to_tenant: "false" }));
              if (r) setDoc({ id: r.id, filename: r.filename });
            }}
          />
          <div>
            <Button type="submit" variant="secondaire" busy={act.busy}>
              Enregistrer le devis
            </Button>
          </div>
        </form>
      ) : null}
    </Card>
  );
}

function InterventionsCard({ orgId, incidentId, quotes, interventions, canOp, onChange }: { orgId: string; incidentId: string; quotes: any[]; interventions: any[]; canOp: boolean; onChange: () => void }) {
  const accepted = quotes.filter((q) => q.status === "accepte");
  const [plan, setPlan] = useState({ quote_id: "", contractor_name: "", at: "", notes: "" });
  const [done, setDone] = useState<any>(null);
  const act = useAction();
  return (
    <Card title="Interventions">
      <ErrorBox error={act.error} />
      {interventions.length ? (
        <Table caption="Interventions">
          <thead>
            <tr>
              <th>Intervenant</th>
              <th>Rendez-vous</th>
              <th>Statut</th>
              <th>Facture</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {interventions.map((v: any) => (
              <tr key={v.id}>
                <td>{v.contractor_name}</td>
                <td>{v.scheduled_at ? dateTime(v.scheduled_at) : "À planifier"}</td>
                <td>
                  <Status value={v.status} labels={INTERVENTION_STATUS} />
                  {v.done_on ? <span className="block small muted">le {date(v.done_on)}</span> : null}
                </td>
                <td>
                  {v.invoice_amount_cents != null ? <span className="vie-amount">{money(v.invoice_amount_cents)}</span> : "—"}
                  <span className="block small muted">{RECOVERABLE[v.recoverable]}</span>
                  {v.invoice_document_id ? (
                    <a className="small" href={docUrl(v.invoice_document_id)} target="_blank" rel="noreferrer">
                      Facture
                    </a>
                  ) : null}
                </td>
                <td>
                  {canOp && v.status === "planifiee" ? (
                    <div className="row">
                      <Button small onClick={() => setDone({ id: v.id, done_on: todayIso(), invoice_amount_cents: null, recoverable: "a_determiner", report: "", doc: null })}>
                        Marquer réalisée
                      </Button>
                      <Button small variant="discret" onClick={async () => { await act.run(() => patch(orgApi(orgId, `/interventions/${v.id}`), { status: "annulee" }), "Intervention annulée."); onChange(); }}>
                        Annuler
                      </Button>
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <p className="muted">Aucune intervention.</p>
      )}
      {canOp ? (
        <form
          className="stack-sm"
          onSubmit={async (e) => {
            e.preventDefault();
            const r = await act.run(
              () => post(orgApi(orgId, `/incidents/${incidentId}/interventions`), { quote_id: plan.quote_id || undefined, contractor_name: plan.contractor_name || undefined, scheduled_at: plan.at ? toIso(plan.at) : undefined, notes: plan.notes }),
              "Intervention enregistrée.",
            );
            if (r) {
              setPlan({ quote_id: "", contractor_name: "", at: "", notes: "" });
              onChange();
            }
          }}
        >
          <h3>Planifier une intervention</h3>
          <div className="form-row">
            <Select label="Devis accepté" value={plan.quote_id} onChange={(e) => setPlan({ ...plan, quote_id: e.target.value })} options={[{ value: "", label: "Sans devis" }, ...accepted.map((q) => ({ value: q.id, label: `${q.contractor_name} — ${money(q.amount_cents)}` }))]} />
            {!plan.quote_id ? <Input label="Intervenant" value={plan.contractor_name} error={fieldError(act.error, "contractor_name")} onChange={(e) => setPlan({ ...plan, contractor_name: e.target.value })} /> : null}
            <Input label="Rendez-vous" type="datetime-local" value={plan.at} onChange={(e) => setPlan({ ...plan, at: e.target.value })} help="Proposez un créneau parmi les disponibilités du locataire." />
          </div>
          <div>
            <Button type="submit" variant="secondaire" busy={act.busy}>
              Planifier
            </Button>
          </div>
        </form>
      ) : null}
      <Modal
        open={!!done}
        onClose={() => setDone(null)}
        title="Intervention réalisée"
        footer={
          <Button
            busy={act.busy}
            onClick={async () => {
              const r = await act.run(
                () =>
                  patch(orgApi(orgId, `/interventions/${done.id}`), {
                    status: "realisee",
                    done_on: done.done_on,
                    report: done.report,
                    invoice_document_id: done.doc?.kind === "facture" ? done.doc.id : undefined,
                    proof_document_id: done.doc?.kind === "compte_rendu" ? done.doc.id : undefined,
                    invoice_amount_cents: done.invoice_amount_cents ?? undefined,
                    recoverable: done.recoverable,
                  }),
                "Intervention marquée réalisée.",
              );
              if (r) {
                setDone(null);
                onChange();
              }
            }}
          >
            Enregistrer
          </Button>
        }
      >
        {done ? (
          <div className="stack">
            <ErrorBox error={act.error} />
            <Notice tone="info">Une intervention n’est marquée réalisée qu’avec une preuve : facture ou compte rendu (photo, PDF).</Notice>
            <Input label="Réalisée le" type="date" value={done.done_on} onChange={(e) => setDone({ ...done, done_on: e.target.value })} />
            <FileDrop
              label={done.doc ? `Pièce jointe : ${done.doc.filename}` : "Déposer la facture"}
              onFiles={async ([f]) => {
                const r = await act.run(() => sendFile(orgApi(orgId, `/incidents/${incidentId}/documents`), f, { kind: "facture", visible_to_tenant: "false" }));
                if (r) setDone({ ...done, doc: { id: r.id, filename: r.filename, kind: "facture" } });
              }}
            />
            <FileDrop
              label="Ou déposer un compte rendu"
              onFiles={async ([f]) => {
                const r = await act.run(() => sendFile(orgApi(orgId, `/incidents/${incidentId}/documents`), f, { kind: "compte_rendu", visible_to_tenant: "true" }));
                if (r) setDone({ ...done, doc: { id: r.id, filename: r.filename, kind: "compte_rendu" } });
              }}
            />
            <MoneyInput label="Montant de la facture" cents={done.invoice_amount_cents} onChange={(c) => setDone({ ...done, invoice_amount_cents: c })} />
            <Select label="Charge récupérable sur le locataire" value={done.recoverable} options={options(RECOVERABLE)} onChange={(e) => setDone({ ...done, recoverable: e.target.value })} help="Indication pour vos comptes, à confirmer selon la nature des travaux." />
            <Textarea label="Compte rendu" value={done.report} onChange={(e) => setDone({ ...done, report: e.target.value })} />
          </div>
        ) : null}
      </Modal>
    </Card>
  );
}

/** Onglet « Incidents » du bail : incidents et demandes d'autorisation de ce bail. */
export function LeaseIncidentsTab({ orgId, leaseId }: { orgId: string; leaseId: string }) {
  const list = useGet<{ items: any[] }>(orgApi(orgId, `/incidents?lease_id=${leaseId}`));
  return (
    <div className="stack">
      {list.isLoading ? <Loading /> : <IncidentTable items={list.data?.items ?? []} link={(id) => `/espace/${orgId}/incidents/${id}`} />}
      <h3>Demandes d’autorisation</h3>
      <OrgAuthorizations orgId={orgId} leaseId={leaseId} />
    </div>
  );
}

/** Section « Signaler un incident » de la location, côté locataire. */
export function TenantIncidentSection({ leaseId }: { leaseId: string }) {
  const list = useGet<{ items: any[]; emergency_notice: string }>(meLease(leaseId, "/incidents"));
  const invalidate = useInvalidate();
  const [selected, setSelected] = useState("");
  const [notice, setNotice] = useState<string | undefined>();
  const [photoFor, setPhotoFor] = useState<string>("");
  return (
    <div className="stack">
      <Card title="Signaler un incident">
        {notice ? <Notice tone="attention">{notice}</Notice> : null}
        <IncidentForm
          path={meLease(leaseId, "/incidents")}
          onCreated={(id, n) => {
            setNotice(n);
            setSelected(id);
            setPhotoFor(id);
            invalidate(meLease(leaseId, "/incidents"));
          }}
        />
      </Card>
      {photoFor ? (
        <Card title="Ajouter des photos">
          <p className="small muted">Les photos aident à qualifier l’incident. Elles sont visibles de votre bailleur uniquement.</p>
          <TenantPhotoUpload leaseId={leaseId} incidentId={photoFor} onDone={() => invalidate(meLease(leaseId, "/incidents"))} />
        </Card>
      ) : null}
      <Card title="Mes signalements">
        {list.isLoading ? (
          <Loading />
        ) : list.error ? (
          <ErrorBox error={list.error} />
        ) : list.data?.items.length ? (
          <ul className="vie-list">
            {list.data.items.map((i) => (
              <li key={i.id}>
                <button type="button" className="vie-list-item" aria-current={selected === i.id ? "true" : undefined} onClick={() => setSelected(i.id)}>
                  <span className="row-between">
                    <strong>{i.title}</strong>
                    <Status value={i.status} labels={INCIDENT_STATUS} />
                  </span>
                  <span className="small muted">
                    {CATEGORIES[i.category]} · {dateTime(i.created_at)}
                    {i.next_intervention_at ? ` · intervention le ${dateTime(i.next_intervention_at)}` : ""}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">Aucun signalement.</p>
        )}
      </Card>
      {selected ? <TenantIncident leaseId={leaseId} incidentId={selected} /> : null}
      <TenantEquipments leaseId={leaseId} />
    </div>
  );
}

function TenantPhotoUpload({ leaseId, incidentId, onDone }: { leaseId: string; incidentId: string; onDone: () => void }) {
  const act = useAction();
  return (
    <>
      <ErrorBox error={act.error} />
      <FileDrop
        multiple
        accept="image/jpeg,image/png,image/webp,image/heic"
        label="Ajouter des photos"
        help="JPEG, PNG, WebP ou HEIC, 15 Mo au maximum par photo."
        busy={act.busy}
        onFiles={async (files) => {
          for (const f of files) await act.run(() => sendFile(meLease(leaseId, `/incidents/${incidentId}/photos`), f), "Photo ajoutée.");
          onDone();
        }}
      />
    </>
  );
}

function TenantIncident({ leaseId, incidentId }: { leaseId: string; incidentId: string }) {
  const path = meLease(leaseId, `/incidents/${incidentId}`);
  const q = useGet<any>(path);
  const invalidate = useInvalidate();
  const [comment, setComment] = useState("");
  const act = useAction();
  if (!q.data) return q.isLoading ? <Loading /> : <ErrorBox error={q.error} />;
  const { incident: i, events, documents, interventions } = q.data;
  const refresh = () => invalidate(path, meLease(leaseId, "/incidents"));
  return (
    <Card title={i.title} actions={<Status value={i.status} labels={INCIDENT_STATUS} />}>
      <div className="stack">
        {interventions.length ? (
          <ul className="list-plain">
            {interventions.map((v: any) => (
              <li key={v.id}>
                {v.contractor_name} : {v.scheduled_at ? `rendez-vous le ${dateTime(v.scheduled_at)}` : "rendez-vous à fixer"} · {INTERVENTION_STATUS[v.status]}
              </li>
            ))}
          </ul>
        ) : null}
        <Timeline items={events.map((e: any) => ({ id: e.id, at: e.created_at, who: e.author_label, text: e.body }))} />
        <DocLinks docs={documents} />
        <ErrorBox error={act.error} />
        {i.status === "resolu" && !i.tenant_confirmed_at ? (
          <Notice tone="info" title="Le bailleur indique que l’incident est résolu">
            <div className="row">
              <Button small busy={act.busy} onClick={async () => { await act.run(() => post(`${path}/confirm`, { resolved: true }), "Merci pour votre confirmation."); refresh(); }}>
                Je confirme la résolution
              </Button>
              <Button
                small
                variant="secondaire"
                onClick={async () => {
                  if (!comment.trim()) return act.setError(new Error("Décrivez ce qui persiste dans le champ de commentaire."));
                  await act.run(() => post(`${path}/confirm`, { resolved: false, comment }), "Le bailleur est informé que le problème persiste.");
                  setComment("");
                  refresh();
                }}
              >
                Le problème persiste
              </Button>
            </div>
          </Notice>
        ) : null}
        {!["resolu", "annule"].includes(i.status) ? <TenantPhotoUpload leaseId={leaseId} incidentId={incidentId} onDone={refresh} /> : null}
        <form
          className="stack-sm"
          onSubmit={async (e) => {
            e.preventDefault();
            if (await act.run(() => post(`${path}/comments`, { body: comment }), "Commentaire envoyé.")) {
              setComment("");
              refresh();
            }
          }}
        >
          <Textarea label="Ajouter une précision" value={comment} onChange={(e) => setComment(e.target.value)} />
          <div>
            <Button type="submit" variant="secondaire" busy={act.busy}>
              Envoyer
            </Button>
          </div>
        </form>
      </div>
    </Card>
  );
}
